import StatisticalCard, {
    StatisticalCardProps,
    StatisticalCardSkeleton,
} from '@/components/CardStatistical'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import ContractNewIcons from '@/icons/ContractNewIcons'
import ContractPendingIcons from '@/icons/ContractPendingIcons'
import CoolleadIcons from '@/icons/CoolleadIcons'
import ErrorIcons from '@/icons/ErrorIcons'
import { CUSTOMER_CONTRACT_TYPE_LABELS } from '@/modules/Contract/constants'
import { getContractCustomersOverviewType } from '@/modules/Contract/services/customer'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Statistical = () => {
    const { t } = useTranslation()
    const { type } = useParams<{ type: string }>()
    const [searchParams] = useSearchParams()
    const filters = searchParams.get('filters') || ''

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS, 'list', 'statistical', filters],
        queryFn: () => getContractCustomersOverviewType(filters || ''),
        loadingComponent: <StatisticalSectionSkeleton />,
    })

    const dataStatistical: Partial<StatisticalCardProps>[] = useMemo(
        () => [
            {
                label: CUSTOMER_CONTRACT_TYPE_LABELS.Created,
                icon: <ContractNewIcons />,
                color: 'warning',
                value: data?.result.contract_new,
                href: generatePathDatacentral(
                    paths.customerContractOverview,
                    {
                        type: 'new',
                    },
                    {
                        filters: filters,
                    }
                ),
            },
            {
                label: CUSTOMER_CONTRACT_TYPE_LABELS.Exchanging,
                icon: <ContractPendingIcons />,
                color: 'processing',
                value: data?.result.contract_exchanging,
                href: generatePathDatacentral(
                    paths.customerContractOverview,
                    {
                        type: 'exchanging',
                    },
                    {
                        filters: filters,
                    }
                ),
            },
            {
                label: CUSTOMER_CONTRACT_TYPE_LABELS.Expiried,
                value: data?.result.contract_expiried,
                icon: <ErrorIcons />,
                color: 'error',
                href: generatePathDatacentral(
                    paths.customerContractOverview,
                    {
                        type: 'expiried',
                    },
                    {
                        filters: filters,
                    }
                ),
            },
            {
                label: CUSTOMER_CONTRACT_TYPE_LABELS.Uploading,
                icon: <CoolleadIcons />,
                color: 'lead',
                value: data?.result.contract_uploading,
                href: generatePathDatacentral(
                    paths.customerContractOverview,
                    {
                        type: 'uploading',
                    },
                    {
                        filters: filters,
                    }
                ),
            },
            {
                label: CUSTOMER_CONTRACT_TYPE_LABELS.Completed,
                value: data?.result.contract_completed,
                color: 'success',
                icon: <CheckCircleIcons />,
                href: generatePathDatacentral(
                    paths.customerContractOverview,
                    {
                        type: 'completed',
                    },
                    {
                        filters: filters,
                    }
                ),
            },
        ],
        [data]
    )

    const mainContractType = useMemo(() => {
        return (
            dataStatistical?.find(
                (contractType) => contractType?.label === `contract-${type}`
            ) || dataStatistical[0]
        )
    }, [type, dataStatistical])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <div className="mt-2">
            <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2">
                <div className="col-span-1">
                    <StatisticalCard
                        {...mainContractType}
                        minimize={false}
                        type="highlighted"
                        label={t(mainContractType?.label || '')}
                    />
                </div>
                <div className="col-span-1 h-full">
                    <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2 h-full">
                        {dataStatistical
                            ?.filter(
                                (contractType) =>
                                    contractType?.label !== `contract-${type}`
                            )
                            ?.map((i, index) => {
                                return (
                                    <StatisticalCard
                                        key={index}
                                        {...i}
                                        minimize={true}
                                        label={t(i.label || '')}
                                    />
                                )
                            })}
                    </div>
                </div>
            </div>
        </div>
    )
}

const StatisticalSectionSkeleton = () => {
    return (
        <div className="mt-2">
            <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2">
                <div className="col-span-1">
                    <StatisticalCardSkeleton highlighted />
                </div>
                <div className="col-span-1 h-full">
                    <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2 h-full">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <StatisticalCardSkeleton key={index} minimize />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Statistical
